import * as admin from '#actions/admin';
import * as affiliate from '#actions/affiliate';
import * as alert from '#actions/alert';
import * as auth from '#actions/auth';
import * as blogAPI from '#actions/blog';
import * as campsite from '#actions/campsite';
import * as payment from '#actions/payment';
import * as contact from '#actions/contact';
import * as event from '#actions/event';
import * as feedback from '#actions/feedback';
import * as gc from '#actions/giftCards';
import * as outing from '#actions/outing';
import * as plans from '#actions/plan';
import * as scan from '#actions/scan';
import * as search from '#actions/search';
import * as stat from '#actions/stat';
import * as sub from '#actions/sub';
import * as trackerAction from '#actions/tracker';
import * as user from '#actions/user';
import * as util from '#actions/util';
import * as support from '#actions/support';
import * as tracking from '#actions/tracking';

import {
  CHECKOUT_STAGES,
  INITIAL_STATE,
  COUNTRY_CURRENCY_MAP,
  flexiForPlanChange,
} from '#constants';

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { getTimeDifferenceInHours } from '#utils';
import { persist } from 'zustand/middleware';
import produce from 'immer';
import { v4 as uuidv4 } from 'uuid';

const store = (set, get) => ({
  user: null,
  sub: {},
  subs: [],
  plans: [],
  steps: [], // tells what the next step is going to be
  setSteps: (steps) => set({ steps }, false, { type: 'setSteps', steps }),
  getPlans: () => plans.getPlans(set, get),
  getPlanCost: (planSlug) => {
    const state = get();
    const plan = state.plans.find((p) => p.slug === planSlug);
    if (!plan) return 0;
    const currency = COUNTRY_CURRENCY_MAP[state.location.country];
    const cost =
      plan.cost.find((c) => c.currency === currency)?.amount ??
      plan.cost[0].amount;
    return cost;
  },
  getCheckoutCost: () => {
    const planSlug = get().sub?.plan?.slug || get().planSlug;
    const planCost = get().getPlanCost(planSlug);
    const promotion = get().promotion;
    const percentOff = promotion?.code?.coupon?.percent_off ?? 0;
    const cost = planCost * (1 - percentOff / 100);
    return cost;
  },
  setUser: (user) => {
    set({ user }, false, { type: 'setUser', user });
  },
  updateUser: ({ user, calle } = {}) =>
    auth.updateUser(set, get, { user, calle }),
  planSlug: '',
  savedFlexiDays: null,
  setPlanSlug: (slug) =>
    set(
      produce((state) => {
        state.planSlug = slug;
        const next = flexiForPlanChange({
          slug,
          flexiDaysBefore: state.trackerInput.flexiDaysBefore,
          flexiDaysAfter: state.trackerInput.flexiDaysAfter,
          savedFlexiDays: state.savedFlexiDays,
        });
        state.trackerInput.flexiDaysBefore = next.flexiDaysBefore;
        state.trackerInput.flexiDaysAfter = next.flexiDaysAfter;
        state.savedFlexiDays = next.savedFlexiDays;
      }),
      false,
      { type: 'setPlanSlug', slug }
    ),
  trackErrors: {},
  setTrackErrors: (trackErrors) =>
    set({ trackErrors }, false, { type: 'setTrackErrors', trackErrors }),
  loading: null,
  loadingMessage: null,
  // Note: setLoading(false) will automatically clear loadingMessage
  setLoading: (loading) =>
    set(
      { loading, loadingMessage: loading ? get().loadingMessage : null },
      false,
      { type: 'setLoading', loading }
    ),
  setLoadingMessage: (loadingMessage) =>
    set({ loadingMessage }, false, {
      type: 'setLoadingMessage',
      loadingMessage,
    }),
  fetchStatus: null,
  setFetchStatus: (fetchStatus) =>
    set({ fetchStatus }, false, { type: 'setFetchStatus', fetchStatus }),
  entryModal: false,
  toggleEntryModal: (entryModal) =>
    set({ entryModal: entryModal ?? !get().entryModal }, false, {
      type: 'setEntryModal',
      entryModal,
    }),
  searchInput: '',
  trackerInput: INITIAL_STATE.trackerInput,
  setSearchInput: (val) => {
    set({ searchInput: val }, false, 'setSearchInput');
  },
  outing: {},
  setOuting: (outing) => set({ outing }, false, 'setOuting'),
  setStartDate: (val) => {
    set(
      produce((state) => {
        state.trackerInput.startDate = val;
      }),
      false,
      'setStartDate'
    );
  },
  setEndDate: (val) => {
    set(
      produce((state) => {
        state.trackerInput.endDate = val;
      }),
      false,
      'setEndDate'
    );
  },
  setEquipmentType: (val) => {
    set(
      produce((state) => {
        state.trackerInput.equipmentType = val;
      }),
      false,
      'setEquipmentType'
    );
  },

  setGroupSize: (val) => {
    set(
      produce((state) => {
        state.trackerInput.groupSize = val;
      }),
      false,
      'setGroupSize'
    );
  },
  setFlexiDays: (val) => {
    set(
      produce((state) => {
        state.trackerInput.flexiDaysBefore = val;
        state.trackerInput.flexiDaysAfter = val;
        // Remember the user's chosen window so PAYG clamp can restore it later.
        if (state.planSlug !== 'pay-as-you-go') {
          state.savedFlexiDays = val;
        }
      }),
      false,
      'setFlexiDays'
    );
  },
  setAccessible: (val) => {
    set(
      produce((state) => {
        state.trackerInput.filters.accessible = val;
      }),
      false,
      { type: 'setAccessible', val }
    );
  },
  addCampsitesToFilter: (val) => {
    set(
      produce((state) => {
        state.trackerInput.filters.campsites.push(val);
      }),
      false,
      { type: 'addCampsitesToFilter', val }
    );
  },
  removeCampsitesFromFilter: (val) => {
    set(
      produce((state) => {
        const campsites = state.trackerInput.filters.campsites;
        const campIndex = campsites?.indexOf(val);
        campsites.splice(campIndex, 1);
      }),
      false,
      { type: 'removeCampsitesFromFilter', val }
    );
  },
  resetCampsitesFilter: () => {
    set(
      produce((state) => {
        state.trackerInput.filters.campsites = [];
        state.trackerInput.filters.permitModes = [];
      }),
      false,
      { type: 'resetCampsitesFilter' }
    );
  },
  /** Clear multi-night trip fields left on trackerInput after a trip draft. */
  clearTripDraft: () => {
    set(
      produce((state) => {
        delete state.trackerInput.type;
        delete state.trackerInput.nights;
      }),
      false,
      { type: 'clearTripDraft' }
    );
  },
  setPermitModeFilter: (mode) => {
    set(
      produce((state) => {
        const cur = state.trackerInput.filters.permitModes?.[0];
        if (cur === mode) {
          state.trackerInput.filters.permitModes = [];
        } else {
          state.trackerInput.filters.permitModes = [mode];
        }
      }),
      false,
      { type: 'setPermitModeFilter', mode }
    );
  },
  setPermitModes: (modes) => {
    set(
      produce((state) => {
        state.trackerInput.filters.permitModes = Array.isArray(modes)
          ? [...modes]
          : [];
      }),
      false,
      { type: 'setPermitModes', modes }
    );
  },
  setNotificationPreference: (val) => {
    set(
      produce((state) => {
        state.trackerInput.notificationPref = val;
      }),
      false,
      { type: 'setNotificationPreference' }
    );
  },
  setBookingUrl: (val) => {
    console.log('setBookingUrl', val);
    set(
      produce((state) => {
        if (!state.trackerInput.metadata) {
          state.trackerInput.metadata = {};
        }
        state.trackerInput.metadata.bookingUrl = val;
      }),
      false,
      { type: 'setBookingUrl', val }
    );
  },
  suggestedResults: [],
  /** Trip-mode parents matching the current tracker search (for dropdown CTA). */
  matchedTripParents: [],
  getSuggestedResults: (query) => search.suggestAction(set, get, { query }),
  tripResults: [],
  noTripResults: false,
  getTripSuggestions: (query) => search.tripSuggestAction(set, get, { query }),
  setTrackerInput: (trackerInput) =>
    set({ trackerInput }, false, 'setTrackerInput'),
  noSearchResults: false,
  showSuggestions: false,
  setShowSuggestions: (val) => {
    set({ showSuggestions: val }, false, 'setShowSuggestions');
  },
  showContactModal: false,
  contactModalError: '',
  toggleContactModal: (showContactModal) =>
    set(
      { showContactModal: showContactModal ?? !get().showContactModal },
      false,
      {
        type: 'toggleEntryModal',
        showContactModal,
      }
    ),
  contactStatus: null,
  setContactStatus: (status) =>
    set({ contactStatus: status }, false, 'setContactStatus'),
  contactInput: {
    contactId: '',
    type: 'phone',
  },
  hasEmail: false,
  setContactIdInput: (contactId) => {
    set(
      produce((state) => {
        state.contactInput.contactId = contactId;
      }),
      false,
      {
        type: 'setContactInput',
        contactId,
      }
    );
  },
  setHasEmail: (hasEmail) => {
    set({ hasEmail }, false, { type: 'setHasEmail', hasEmail });
  },
  showPricingModal: false,
  setShowPricingModal: (val) => {
    set({ showPricingModal: val }, false, 'setShowPricingModal');
  },
  togglePricingModal: (showPricingModal) =>
    set(
      { showPricingModal: showPricingModal ?? !get().showPricingModal },
      false,
      {
        type: 'togglePricingModal',
        showPricingModal,
      }
    ),
  showExitPopup: false,
  toggleExitPopup: (showExitPopup) =>
    set({ showExitPopup: showExitPopup ?? !get().showExitPopup }, false, {
      type: 'toggleExitPopup',
      showExitPopup,
    }),
  registerContact: () => contact.register(set, get),
  verifyContact: ({ otp }) => contact.verifyContact({ otp }, set, get),
  createTracker: ({ subId }) =>
    trackerAction.createTracker(set, get, { subId }),
  oAuthLogin: (credential) => auth.oAuthLogin(set, get, { credential }),
  adminLogin: ({ email, id, password }) =>
    auth.adminLogin(set, get, { email, id, password }),
  logoutUser: () => auth.logoutUser(set, get),
  toast: {},
  setToast: (toast) => set({ toast }, false, { type: 'setToast', toast }),
  codeCheckout: () => payment.codeCheckout(set, get),
  setCoupon: (coupon) => set({ coupon }, false, { type: 'setCoupon', coupon }),
  card: null,
  setCard: (card) => set({ card }, false, { type: 'setCard', card }),
  cardError: '',
  setCardError: (cardError) =>
    set({ cardError }, false, { type: 'setCardError', cardError }),
  paymentMode: 'card',
  setPaymentMode: (mode) =>
    set({ paymentMode: mode }, false, { type: 'setPaymentMode', mode }),
  createSub: (sub) => sub.create(set, get, { sub }),
  address: {
    firstName: '',
    lastName: '',
    line1: '',
    line2: '',
    state: '',
    country: 'US',
    city: '',
    zipcode: '',
  },
  setAddress: (address) =>
    set({ address }, false, { type: 'setAddress', address }),
  getTrackers: ({ page, size, sortBy, order, adminView } = {}) =>
    trackerAction.getTrackers(set, get, {
      page,
      size,
      sortBy,
      order,
      adminView,
    }),
  getTracker: ({ shortId }) => trackerAction.getTracker(set, get, { shortId }),
  getNotifications: () => trackerAction.getNotifications(set, get),
  notifications: INITIAL_STATE.notifications,
  trackers: [],
  tracker: {},
  updateTrackerStatus: ({ status, _id, adminRefetch, notes }) =>
    trackerAction.updateTrackerStatus(set, get, {
      status,
      _id,
      adminRefetch,
      notes,
    }),
  updateTracker: ({ tracker, notes }) =>
    trackerAction.updateTracker(set, get, { tracker, notes }),
  updateTrackerShortStatus: ({ shortId, booked }) =>
    trackerAction.updateTrackerShortStatus(set, get, { shortId, booked }),
  updateSubStatus: ({ status, _id, period, paymentId, customPayment }) =>
    sub.updateSubStatus(set, get, {
      status,
      _id,
      period,
      paymentId,
      customPayment,
    }),
  getSub: () => sub.getSub(set, get),
  getUpgradePreview: ({ subscriptionId }) =>
    sub.getUpgradePreview(set, get, { subscriptionId }),
  upgradeSubscription: ({ subscriptionId }) =>
    sub.upgradeSubscription(set, get, { subscriptionId }),
  payments: {},
  setPayments: (payments) =>
    set({ payments }, false, { type: 'setPayments', payments }),
  stripePayments: {
    clientSecret: null,
    message: null,
  },
  setStripePayments: (stripePayments) =>
    set({ stripePayments }, false, { type: 'stripePayments', stripePayments }),
  createPaymentIntent: () => payment.createPaymentIntent(set, get),
  removePaymentIntent: () => payment.removePaymentIntent(set, get),
  onPaymentSuccess: ({ subId, paymentId }) =>
    payment.onPaymentSuccess(set, get, { subId, paymentId }),
  profileTab: 'trackers',
  setProfileTab: (profileTab) =>
    set({ profileTab }, false, { type: 'setProfileTab', profileTab }),
  giftCards: [],
  getGiftCards: () => gc.getGiftCards(set, get),
  checkoutStage: CHECKOUT_STAGES[0],
  setCheckoutStage: (stage) =>
    set({ checkoutStage: stage }, false, { type: 'setCheckoutStage', stage }),
  reviewOrder: () => payment.reviewOrder(set, get),
  checkout: {},
  campsites: [],
  getCampsites: () => campsite.getCampsites(set, get),
  setCampsites: (campsites) =>
    set({ campsites }, false, { type: 'setCampsites', campsites }),
  campsitesModal: false,
  toggleCampsitesModal: (campsitesModal) =>
    set({ campsitesModal: campsitesModal ?? !get().campsitesModal }, false, {
      type: 'toggleCampsitesModal',
      campsitesModal,
    }),
  getOuting: ({ slug }) => outing.getOuting({ slug }, set, get),
  getNearbyOutings: (filters) => outing.getNearbyOutings(filters, set, get),
  nearbyOutings: [],
  setNearbyOutings: (nearbyOutings) =>
    set({ nearbyOutings }, false, 'setNearbyOuting'),
  stateInfo: {
    areas: [],
    popularCampgrounds: [],
    campgrounds: [],
    // popularActivities: [],
    activities: [],
  },
  getStateOutings: ({ state }) => outing.getStateOutings({ state }, set, get),
  setStateOutings: (data) =>
    set({ stateInfo: data }, false, { type: 'setStateOutings' }),
  blog: {
    title: '',
    author: '',
    avatar: '',
    thumbnail: '',
    hero: '',
    summary: '',
    tags: [],
    content: '',
    isPublished: false,
  },
  blogs: [],
  setBlog: (blog) => set({ blog }, false, 'setBlog'),
  setBlogs: (blogs) => set({ blogs }, false, 'setBlogs'),
  upsertBlog: ({ blog }) => blogAPI.upsertBlog({ blog }, set, get),
  getBlog: ({ slug }) => blogAPI.getBlog({ slug }, set, get),
  getBlogs: ({ page, size }) => blogAPI.getBlogs({ page, size }, set, get),
  generateSitemap: () => admin.generateSitemap(set, get),
  currentPage: '',
  setCurrentPage: ({ page }) =>
    set({ currentPage: page }, false, 'currentPage'),
  updateUserPref: ({ preference, address, email, firstName, lastName, secondaryPhone }) =>
    user.updateUserPref(set, get, {
      preference,
      address,
      email,
      firstName,
      lastName,
      secondaryPhone,
    }),
  getAffiliate: () => affiliate.getAffiliate(set, get),
  affiliate: {},
  location: INITIAL_STATE.location,
  getCurrentLocation: () => util.getCurrentLocation(set, get),
  stateRecs: [],
  getStateRecs: ({ stateCode }) => util.getStateRecs(set, get, { stateCode }),
  tawk: null,
  setTawk: (tawkRef) => set({ tawk: tawkRef }, false, 'setTawk'),
  syncTawkVisitor: () => util.syncTawkVisitor(set, get),
  recReview: {},
  recSearchResults: [],
  getRecReview: () => admin.getRecReview(set, get),
  getRecSearch: (params) => admin.getRecSearch(params, set, get),
  addRecOuting: (payload) => admin.addRecOuting(payload, set, get),
  updatedSafeOutings: () => admin.updatedSafeOutings(set, get),
  saveRecReview: ({ outing, recOuting }) =>
    admin.saveRecReview({ outing, recOuting }, set, get),
  campReview: {},
  getCampReview: () => admin.getCampReview(set, get),
  updatedSafeCamps: () => admin.updatedSafeCamps(set, get),
  updatedSafePermits: () => admin.updatedSafePermits(set, get),
  updatePermalinks: () => admin.updatePermalinks(set, get),
  addYSLodges: () => admin.addYSLodges(set, get),
  addGCLodges: () => admin.addGCLodges(set, get),
  syncTourSlots: () => admin.syncTourSlots(set, get),
  syncBrevo: () => admin.syncBrevo(set, get),
  permitMapping: [],
  getPermitMapping: ({ page, size }) =>
    admin.getPermitMapping({ page, size }, set, get),
  updatePermit: ({ permit, crawlerType }) =>
    admin.updatePermit({ permit, crawlerType }, set, get),
  updateOffer: ({ emails }) => admin.updateOffer({ emails }, set, get),
  getDashData: () => admin.getDashData(set, get),
  getAdTraffic: (opts) => admin.getAdTraffic(set, opts),
  getFunnelData: (range) => admin.getFunnelData(set, get, range),
  dashData: {},
  adTraffic: null,
  adTrafficLoading: false,
  funnelData: null,
  funnelDataLoading: false,
  putEvent: ({ type, data }) => event.putEvent({ type, data }, set, get),
  postFeedback: ({ description, name, user, tracker }) =>
    feedback.postFeedback({ description, name, user, tracker }, set, get),
  fetchTestimonials: () => feedback.fetchTestimonials(set, get),
  getStat: ({ type }) => stat.getStat({ type }, set, get),
  stats: [],
  // Scans
  scans: {
    page: 0,
    total: 0,
    size: 0,
    data: [],
  },
  getScans: ({ trackerId, page, size }) =>
    scan.getScans({ trackerId, page, size }, set, get),
  alerts: {
    page: 0,
    total: 0,
    size: 0,
    data: [],
  },
  getAlerts: ({ trackerId, page, size }) =>
    alert.getAlerts({ trackerId, page, size }, set, get),
  alert: {
    show: false,
    pages: [],
    text: '',
    status: '',
  },
  setAlert: (alert) => set({ alert }, false, { type: 'setAlert', alert }),
  code: '',
  setCode: (code) => set({ code }, false, { type: 'setCode', code }),
  selectedTracker: {},
  setSelectedTracker: (tracker) =>
    set({ selectedTracker: tracker }, false, {
      type: 'setSelectedTracker',
      tracker,
    }),
  sessionInfo: INITIAL_STATE.sessionInfo,
  resetStore: () => util.resetStore(set, get),
  showSubActionModal: false,
  toggleSubActionModal: (showSubActionModal) =>
    set(
      { showSubActionModal: showSubActionModal ?? !get().showSubActionModal },
      false,
      {
        type: 'toggleEntryModal',
        showSubActionModal,
      }
    ),
  editModal: false,
  toggleEditModal: (editModal) =>
    set({ editModal: editModal ?? !get().editModal }, false, {
      type: 'setEditModal',
      editModal,
    }),
  news: {
    show: false,
    text: '',
  },
  setNews: (news) => set({ news }, false, { type: 'setNews', news }),
  chat: ({ messages }) => support.chat({ messages }, set, get),
  promotion: {},
  setPromotion: (promotion) =>
    set({ promotion }, false, { type: 'setPromotion', promotion }),
  getPromotion: ({ promotionCode }) =>
    payment.getPromotion({ promotionCode }, set, get),
  banner: {}, // of the type { message, type}
  setBanner: (banner) => set({ banner }, false, { type: 'setBanner', banner }),
  selectOuting: ({ selectedOuting, searchInput, name }) =>
    outing.selectOuting({ selectedOuting, searchInput, name }, set, get),
  getOutingStats: ({ outingId } = {}) =>
    outing.getOutingStats({ outingId }, set, get),
  postLogin: ({ token, user, planSlug }) =>
    auth.postLogin(set, get, { token, user, planSlug }),
  email: '',
  setEmail: (email) => set({ email }, false, { type: 'setEmail', email }),
  emailError: '',
  setEmailError: (emailError) =>
    set({ emailError }, false, { type: 'setEmailError', emailError }),
  sendFBEvent: ({ eventName, params }) =>
    tracking.sendFBEvent(set, get, { eventName, params }),
});

const immer = (config) => (set, get, api) =>
  config(
    (partial, replace) => {
      const nextState =
        typeof partial === 'function' ? produce(partial) : partial;
      return set(nextState, replace);
    },
    get,
    api
  );

const persistParams = {
  name: 'reserve-nature',
  partialize: (state) => ({
    // Make sure you  are resetting all these in logout function
    planSlug: state.planSlug,
    savedFlexiDays: state.savedFlexiDays,
    trackerInput: state.trackerInput,
    searchInput: state.searchInput,
    outing: state.outing,
    sessionInfo: state.sessionInfo,
    user: state.user,
    news: state.news,
  }),
  merge: (persistedState, currentState) => {
    // Handle date merges
    const newState = { ...currentState, ...persistedState };
    const {
      trackerInput: { startDate, endDate },
    } = persistedState;
    if (startDate) {
      newState.trackerInput.startDate = new Date(startDate);
    }
    if (startDate) {
      newState.trackerInput.endDate = new Date(endDate);
    }
    const sessionInfo = persistedState.sessionInfo ?? {};
    sessionInfo.createdAt = new Date(sessionInfo.createdAt);
    if (!sessionInfo.id) {
      sessionInfo.id = uuidv4();
      sessionInfo.createdAt = new Date();
    }
    newState.sessionInfo = sessionInfo;
    const filters = newState.trackerInput?.filters;
    if (filters && !Array.isArray(filters.permitModes)) {
      filters.permitModes = [];
    }
    return newState;
  },
};

// const useUIStore = create(devtools(persist(immer(store), persistParams)));
const storeImpl = create(
  subscribeWithSelector(devtools(persist(store, persistParams)))
);

// Sync Tawk visitor only when user (name, email, etc.) changes
storeImpl.subscribe(
  (state) => state.user,
  () => storeImpl.getState().syncTawkVisitor?.()
);

export const useUIStore = storeImpl;
